/**
 * Racine de l'application : fournisseurs (Auth, Public), routage et garde d'authentification.
 */
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { PublicModeProvider } from '@/context/PublicModeContext';
import { OfflineProvider } from '@/context/OfflineContext';
import { NavGuardProvider } from '@/context/NavGuardContext';
import Layout from '@/app/Layout';
import RouteTitle from '@/app/RouteTitle';
import { Spinner } from '@/components/ui/kit';

import Login from '@/features/auth/Login';
import ResetPassword from '@/features/auth/ResetPassword';
import Dashboard from '@/features/dashboard/Dashboard';
import Stock from '@/features/cave/Stock';
import Alertes from '@/features/cave/Alertes';
import Mouvements from '@/features/cave/Mouvements';
import Degustes from '@/features/cave/Degustes';
import Inventaire from '@/features/cave/Inventaire';
import EditionGroupee from '@/features/cave/EditionGroupee';
import FicheCuvee from '@/features/cuvee/FicheCuvee';
import Vignobles from '@/features/vignobles/Vignobles';
import DomainesList from '@/features/vignobles/DomainesList';
import FicheDomaine from '@/features/vignobles/FicheDomaine';
import Decouvrir from '@/features/vignobles/Decouvrir';
import Appellations from '@/features/vignobles/Appellations';
import CaveAppellations from '@/features/vignobles/CaveAppellations';
import ComingSoon from '@/features/misc/ComingSoon';
import Scanner from '@/features/scanner/Scanner';
import Sortie from '@/features/sortie/Sortie';
import Piloter from '@/features/piloter/Piloter';
import Valorisation from '@/features/piloter/Valorisation';
import Rapports from '@/features/piloter/Rapports';
import Composer from '@/features/carte/Composer';
import Traductions from '@/features/carte/Traductions';
import Editions from '@/features/carte/Editions';
import PublicCarte from '@/features/carte/PublicCarte';
import Parametres from '@/features/parametres/Parametres';
import Achats from '@/features/achats/Achats';
import AdminApp from '@/features/admin/AdminApp';
import { ImpersonateLanding } from '@/features/admin/Impersonate';
import ForcePasswordChange from '@/features/auth/ForcePasswordChange';

/** Garde : redirige vers /login si non authentifié. */
function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
  if (user === null) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

/** Garde : espace métier réservé aux comptes d'établissement (le super-admin va vers /admin). */
function RequireTenant({ children }) {
  const { user } = useAuth();
  if (user?.is_superadmin) return <Navigate to="/admin" replace />;
  return children;
}

/** Garde : espace super-admin. */
function RequireSuperadmin({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
  if (user === null) return <Navigate to="/login" replace />;
  if (!user.is_superadmin) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  return (
    <Layout>
      <ForcePasswordChange />
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/scanner" element={<Scanner />} />
        <Route path="/sortie" element={<Sortie />} />
        <Route path="/cave" element={<Navigate to="/cave/stock" replace />} />
        <Route path="/cave/stock" element={<Stock />} />
        <Route path="/cave/alertes" element={<Alertes />} />
        <Route path="/cave/mouvements" element={<Mouvements />} />
        <Route path="/cave/degustes" element={<Degustes />} />
        <Route path="/cave/inventaire" element={<Inventaire />} />
        <Route path="/cave/edition-groupee" element={<EditionGroupee />} />
        <Route path="/cave/cuvee/:id" element={<FicheCuvee />} />
        <Route path="/piloter" element={<Piloter />} />
        <Route path="/piloter/valorisation" element={<Valorisation />} />
        <Route path="/piloter/rapports" element={<Rapports />} />
        <Route path="/carte" element={<Composer />} />
        <Route path="/carte/traductions" element={<Traductions />} />
        <Route path="/carte/editions" element={<Editions />} />
        <Route path="/vignobles" element={<Vignobles />} />
        <Route path="/vignobles/domaines" element={<DomainesList />} />
        <Route path="/vignobles/domaine/:id" element={<FicheDomaine />} />
        <Route path="/vignobles/decouvrir" element={<Decouvrir />} />
        <Route path="/vignobles/appellations" element={<Appellations />} />
        <Route path="/vignobles/mes-appellations" element={<CaveAppellations />} />
        <Route path="/parametres/:rubrique" element={<Parametres />} />
        <Route path="/achats" element={<Navigate to="/achats/a-traiter" replace />} />
        <Route path="/achats/:onglet" element={<Achats />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteTitle />
        <Toaster position="bottom-center" toastOptions={{ style: { fontFamily: 'IBM Plex Sans' } }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/impersonate" element={<ImpersonateLanding />} />
          <Route path="/c/:slug" element={<PublicCarte />} />
          <Route path="/admin/*" element={<RequireSuperadmin><AdminApp /></RequireSuperadmin>} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <RequireTenant>
                  <PublicModeProvider>
                    <OfflineProvider>
                      <NavGuardProvider>
                        <Shell />
                      </NavGuardProvider>
                    </OfflineProvider>
                  </PublicModeProvider>
                </RequireTenant>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
